import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./ExpenseReview.module.scss";
import secondaryComponents from "..";
import useExpenseReview from "./useExpenseReview";
import primaryComponents from "../../primaryComponents";
import { useEffect } from "react";
import Validation from "./Validation";
import { RoutedModal, WarningDialog } from "../../common";

const ExpenseReview = ({
  isUpdate = false,
  expense,
  handleFetchExpenses,
  closeWidget = () => {},
}) => {
  const {
    isLoadingForm,
    expenseDetails,
    expenseCategories,
    storeList,
    isResponding,
    responseStatus,
    disbursementParameters,
    isDisbursmentForm,
    bankList,
    isMakeRefundState,
    expenseDepartments,
    chargeVat,
    isLoadingBankAccountsList,
    bankAccounts,
    setChargeVat,
    handleResolveAccountNumber,
    setExpenseDetails,
    handleExpenseStatus,
    setIsDisbursmentForm,
    handleMakeRefundStatus,
    handleExpenseStatusPr,
    handleCancelRecurrence,
    isCancellingRecurrence,
    isConfirmingCancelRecurrence,
    setIsConfirmingCancelRecurrence,
  } = useExpenseReview({
    closeWidget,
    expense,
    handleFetchExpenses,
  });
  const {
    handleSubmit,
    formState: { errors },
    control,
    trigger,
    register,
    setValue,
  } = Validation({ expenseDetails });

  useEffect(() => {
    window.setExpenseReviewFormValue = setValue;
  }, []);

  return (
    <>
      {!isDisbursmentForm ? (
        <>
          <div className={styles.expenseReview}>
            <div className="mb-3">
              {expenseDetails.make_refund ? (
                <FontAwesomeIcon
                  icon="angle-left"
                  style={{ fontSize: "25px", cursor: "pointer" }}
                  onClick={() => {
                    if (expenseDetails.make_refund) {
                      setExpenseDetails((prevState) => ({
                        ...prevState,
                        make_refund: false,
                      }));
                    }
                  }}
                />
              ) : null}
            </div>

            <div
              className="d-flex ml-auto align-items-center"
              style={{ gap: "10px" }}
            >
              {(expense?.is_recurring || expense?.source === "recurring") && (
                <div
                  className="mb-3 d-flex align-items-center flex-wrap"
                  style={{ gap: "10px" }}
                >
                  {expense?.is_recurring && (
                    <span className="purple--squared">
                      <FontAwesomeIcon icon="redo-alt" className="mr-2" />
                      Recurring
                      {expense?.recurrence_frequency
                        ? ` (${expense.recurrence_frequency})`
                        : ""}
                    </span>
                  )}
                  {expense?.source === "recurring" && (
                    <span className="gray--squared">System-generated</span>
                  )}
                  {expense?.recurrence_cancelled_at && (
                    <span className="error--squared">Recurrence cancelled</span>
                  )}
                </div>
              )}

              {expense?.is_recurring && !expense?.recurrence_cancelled_at && (
                <div className="mb-3">
                  <primaryComponents.Button
                    classNames="btn btn--outline-red smallBtn"
                    type="button"
                    onClick={() => setIsConfirmingCancelRecurrence(true)}
                    isDisabled={isCancellingRecurrence}
                  >
                    Cancel Recurrence
                  </primaryComponents.Button>
                </div>
              )}
            </div>

            <secondaryComponents.LoaderHelper
              isLoading={isLoadingForm}
              classNames="mt-5"
            >
              <form className={styles.expenseReview__form}>
                {!expenseDetails.make_refund ? (
                  <>
                    <primaryComponents.InputFieldV2
                      control={control}
                      classNames="white"
                      label="Expense Name"
                      placeholder="Enter expense name"
                      name="expenseName"
                      value={expenseDetails?.name}
                      isRequired
                      register={register}
                      errorMessage={errors.expenseName}
                      onKeyUp={() =>
                        errors.expenseName !== undefined &&
                        trigger("expenseName")
                      }
                      onChange={({ target: { value } }) => {
                        setExpenseDetails((prevState) => ({
                          ...prevState,
                          name: value,
                        }));
                      }}
                    />
                    <div
                      className="d-flex flex-column flex-md-row"
                      style={{ gap: "10px" }}
                    >
                      <div className="w-100">
                        <primaryComponents.SelectFieldV2
                          control={control}
                          name="store"
                          label="Store"
                          isRequired
                          options={storeList}
                          idKey="id"
                          nameKey="name"
                          value={
                            expenseDetails?.store
                              ? expenseDetails?.store_label
                              : "Select store"
                          }
                          onChange={(value, label) => {
                            setExpenseDetails((prevState) => ({
                              ...prevState,
                              store: value,
                              store_label: label,
                            }));
                          }}
                          classNames="store-select"
                          isSearchable={false}
                          errorMessage={errors.store}
                        />
                      </div>
                      <div className="w-100">
                        <div className="mb-2">
                          <secondaryComponents.LoaderHelper
                            isLoading={isLoadingBankAccountsList}
                            className="mt-1"
                          >
                            <primaryComponents.SelectField
                              options={bankAccounts?.map((bank) => ({
                                id: bank?.id,
                                name: bank?.account_label,
                              }))}
                              label="Fund Expense From"
                              idKey="id"
                              nameKey="name"
                              value={
                                bankAccounts?.map(
                                  (account) =>
                                    `${account?.id}` ===
                                    `${expenseDetails?.ledger_account_id}`,
                                )?.account_label ||
                                "Select account to record this expense"
                              }
                              onChange={(value) => {
                                setExpenseDetails((prev) => ({
                                  ...prev,
                                  ledger_account_id: value,
                                }));
                              }}
                              floatError={false}
                              classNames="bank-name"
                              isSearchable={false}
                            />
                          </secondaryComponents.LoaderHelper>
                        </div>
                      </div>
                    </div>

                    <div
                      className="d-flex flex-column flex-md-row"
                      style={{ gap: "10px" }}
                    >
                      <div className="w-100">
                        <label>
                          <span>Where you charge VAT on this Expense?</span>
                        </label>
                        <div className="d-flex" style={{ gap: "10px" }}>
                          <primaryComponents.BoxedRadioInput
                            name="chargeVat"
                            value={true}
                            id="YesChargeVat"
                            isChecked={chargeVat === true}
                            classNames={`whites ${styles.boxedRadio}`}
                            setRadioValue={() => {
                              setChargeVat(true);
                              setExpenseDetails((prevState) => ({
                                ...prevState,
                                vat_rate: expenseDetails?.vat_rate || 7.5,
                              }));
                            }}
                          >
                            Yes
                          </primaryComponents.BoxedRadioInput>
                          <primaryComponents.BoxedRadioInput
                            name="chargeVat"
                            value={false}
                            id="NoChargeVat"
                            isChecked={chargeVat === false}
                            classNames={`whites ${styles.boxedRadio}`}
                            setRadioValue={() => {
                              setChargeVat(false);
                              setExpenseDetails((prevState) => ({
                                ...prevState,
                                vat_rate: 0,
                              }));
                            }}
                          >
                            No
                          </primaryComponents.BoxedRadioInput>
                        </div>
                      </div>
                      <primaryComponents.AmountFieldV2
                        control={control}
                        classNames="white"
                        label={
                          <span>
                            <span>Amount</span>
                            {chargeVat && (
                              <span className="purple--text small-text-size">
                                (Inclusive of VAT)
                              </span>
                            )}
                          </span>
                        }
                        placeholder="Enter amount"
                        value={expenseDetails?.amount}
                        name="amount"
                        isRequired
                        register={register}
                        errorMessage={errors.amount}
                        onKeyUp={() =>
                          errors.amount !== undefined && trigger("amount")
                        }
                        onChange={({ target: { value } }) => {
                          setExpenseDetails((prevState) => ({
                            ...prevState,
                            amount: value,
                          }));
                        }}
                      />
                    </div>
                    <div
                      className="d-flex flex-column flex-md-row"
                      style={{ gap: "10px" }}
                    >
                      <primaryComponents.VatPercentField
                        label="VAT Rate"
                        name="vat_rate"
                        control={control}
                        isDisabled={!chargeVat}
                        value={expenseDetails?.vat_rate}
                        onChange={({ percentValue }) => {
                          setExpenseDetails((prev) => ({
                            ...prev,
                            vat_rate: percentValue,
                          }));
                        }}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        errorMessage={errors.vat_rate}
                        dividedValue={expenseDetails?.amount || 0}
                      />
                      <primaryComponents.DateInputV2
                        classNames="white"
                        label="Expense Date"
                        control={control}
                        name="expenseDate"
                        isRequired
                        register={register}
                        errorMessage={errors.expenseDate}
                        value={expenseDetails?.expense_date}
                        onKeyUp={() =>
                          errors.expenseDate !== undefined &&
                          trigger("expenseDate")
                        }
                        onChange={({ target: { value } }) => {
                          setExpenseDetails((prevState) => ({
                            ...prevState,
                            expense_date: value,
                          }));
                        }}
                      />
                    </div>
                    <div
                      className="d-flex flex-column flex-md-row"
                      style={{ gap: "10px" }}
                    >
                      <div className="w-100">
                        <primaryComponents.SelectFieldV2
                          label="Category"
                          isRequired
                          options={expenseCategories}
                          nameKey="name"
                          idKey="id"
                          name="category"
                          control={control}
                          value={
                            expenseDetails?.category
                              ? expenseDetails.category_label
                              : "Choose Category"
                          }
                          errorMessage={errors.category}
                          onChange={(value, label) => {
                            setExpenseDetails((prevState) => ({
                              ...prevState,
                              category: value,
                              category_label: label,
                            }));
                          }}
                        />
                      </div>

                      <div className="w-100">
                        <primaryComponents.SelectFieldV2
                          label="Payment Method"
                          isRequired
                          options={[
                            { name: "Transfer", id: "transfer" },
                            { name: "Card", id: "card" },
                            { name: "Cash", id: "cash" },
                          ]}
                          nameKey="name"
                          idKey="id"
                          name="paymentMethod"
                          control={control}
                          value={
                            expenseDetails?.payment_method
                              ? expenseDetails?.payment_method_label
                              : "Select payment method for this expense"
                          }
                          errorMessage={errors.paymentMethod}
                          onChange={(value, label) => {
                            setExpenseDetails((prevState) => ({
                              ...prevState,
                              payment_method: value,
                              payment_method_label: label,
                            }));
                          }}
                        />
                      </div>
                    </div>

                    <div
                      className="d-flex flex-column flex-md-row"
                      style={{ gap: "10px" }}
                    >
                      <div className="w-100">
                        <primaryComponents.SelectFieldV2
                          control={control}
                          label="Department"
                          options={expenseDepartments}
                          nameKey="name"
                          idKey="id"
                          name="department"
                          value={
                            expenseDetails?.department?.name ||
                            "Select Departmemt"
                          }
                        />
                      </div>
                      <div className="w-100">
                        <label>
                          <span>Expense Proof</span>
                        </label>
                        <primaryComponents.Button
                          classNames="btn btn--outline cover"
                          isDisabled={!expenseDetails?.proof_url}
                          type="button"
                          onClick={() =>
                            window.open(expenseDetails?.proof_url, "_blank")
                          }
                        >
                          {expenseDetails?.proof_url
                            ? "View Document"
                            : "No Document"}
                        </primaryComponents.Button>
                      </div>
                    </div>
                    <primaryComponents.TextAreaV2
                      classNames="white"
                      label="Description"
                      placeholder="Describe this expense"
                      control={control}
                      name="description"
                      value={expenseDetails?.description}
                      register={register}
                      onChange={({ target: { value } }) => {
                        setExpenseDetails((prevState) => ({
                          ...prevState,
                          description: value,
                        }));
                      }}
                    />

                    <primaryComponents.TextArea
                      classNames="white"
                      label="Reason for Declination"
                      placeholder="Enter reason for Declination"
                      name="declinationReason"
                      value={expenseDetails?.decline_reason}
                      onChange={({ target: { value } }) => {
                        setExpenseDetails((prevState) => ({
                          ...prevState,
                          decline_reason: value,
                        }));
                      }}
                    />

                    {expense?.payment_method === "PR" && (
                      <secondaryComponents.CountryItemPermission>
                        <div className="mb-3">
                          <label>
                            <span className="text-danger">*</span>
                            <span>
                              Re-imburse or Disburse Funds For This Expense?
                            </span>
                          </label>
                          <div className="d-flex" style={{ gap: "10px" }}>
                            <primaryComponents.BoxedRadioInput
                              name="reImburse"
                              value={true}
                              id="YesReImburse"
                              isChecked={expenseDetails.make_refund}
                              classNames="whites"
                              setRadioValue={() => {
                                handleMakeRefundStatus();
                                setExpenseDetails((prevState) => ({
                                  ...prevState,
                                  make_refund: true,
                                }));
                              }}
                            >
                              Yes
                            </primaryComponents.BoxedRadioInput>
                            <primaryComponents.BoxedRadioInput
                              name="reImburse"
                              value={false}
                              id="NoReImburse"
                              isChecked={!expenseDetails.make_refund}
                              classNames="whites"
                              setRadioValue={() => {
                                setExpenseDetails((prevState) => ({
                                  ...prevState,
                                  make_refund: false,
                                }));
                              }}
                            >
                              No
                            </primaryComponents.BoxedRadioInput>
                          </div>
                        </div>
                      </secondaryComponents.CountryItemPermission>
                    )}
                    <secondaryComponents.NavigateWithPermission
                      permissionParentKey={"Expense"}
                      permissionChildKey="can_approve_expense"
                    >
                      <div className={styles.expenseReview__bottom}>
                        <primaryComponents.Button
                          classNames="btn btn--outline-red cover"
                          onClick={handleSubmit(() =>
                            handleExpenseStatus("decline"),
                          )}
                          isLoading={
                            isResponding && responseStatus === "decline"
                          }
                          isDisabled={
                            isResponding && responseStatus === "decline"
                          }
                          loadingText="Rejecting..."
                        >
                          Reject
                        </primaryComponents.Button>
                        <primaryComponents.Button
                          classNames="btn btn--primary cover"
                          onClick={handleSubmit(() => {
                            if (
                              !expense?.make_refund &&
                              expense?.payment_method === "PR"
                            ) {
                              handleExpenseStatusPr("approve");
                            } else {
                              handleExpenseStatus("approve");
                            }
                          })}
                          isLoading={
                            isResponding && responseStatus === "approve"
                          }
                          isDisabled={
                            isResponding && responseStatus === "approve"
                          }
                          loadingText="Approving..."
                        >
                          {expense?.make_refund ? "Disburse" : "Approve"}
                        </primaryComponents.Button>
                      </div>
                    </secondaryComponents.NavigateWithPermission>
                  </>
                ) : (
                  <>
                    <primaryComponents.SelectField
                      control={control}
                      name="bankName"
                      label="Bank"
                      isRequired
                      options={bankList}
                      nameKey="bank_name"
                      idKey="bank_code"
                      value={"Select bank account"}
                      onChange={(value, label) => {
                        setExpenseDetails((prevState) => ({
                          ...prevState,
                          bank_code: value,
                          bank_code_label: label,
                        }));
                        if (expenseDetails?.account_number.length === 10)
                          handleResolveAccountNumber(
                            value,
                            expenseDetails?.account_number,
                          );
                      }}
                      classNames="bank-select"
                      errorMessage={errors.bankName}
                    />

                    <div>
                      <primaryComponents.InputFieldV2
                        control={control}
                        label="Account Number"
                        isRequired
                        placeholder="Enter 10-digit account number"
                        name="accountNumber"
                        classNames="white"
                        floatError={false}
                        value={expenseDetails?.account_number}
                        onChange={({ target: { value } }) => {
                          setExpenseDetails((prevState) => ({
                            ...prevState,
                            account_number: value,
                          }));
                          if (value.length === 10)
                            handleResolveAccountNumber(
                              expenseDetails?.bank_code,
                              value,
                            );
                        }}
                        onKeyUp={() =>
                          errors.accountNumber !== undefined &&
                          trigger("accountNumber")
                        }
                        register={register}
                        errorMessage={errors.accountNumber}
                      />
                      {expenseDetails?.account_name && (
                        <p className="small-text-size purple--text">
                          {expenseDetails?.account_name}
                        </p>
                      )}
                    </div>
                    <div className={styles.expenseReview__bottom}>
                      <primaryComponents.Button
                        classNames="btn btn--primary cover"
                        onClick={handleMakeRefundStatus}
                        isLoading={isMakeRefundState}
                        loadingText={"Checking..."}
                        isDisabled={isMakeRefundState}
                      >
                        Continue
                      </primaryComponents.Button>
                    </div>
                  </>
                )}
              </form>
            </secondaryComponents.LoaderHelper>

            <RoutedModal
              headerTitle="Cancel this recurring expense?"
              isActive={isConfirmingCancelRecurrence}
              width={500}
              closeModal={() => setIsConfirmingCancelRecurrence(false)}
              style={{ height: "auto" }}
            >
              <WarningDialog
                cancelModal={() => setIsConfirmingCancelRecurrence(false)}
                closeModal={() => setIsConfirmingCancelRecurrence(false)}
                handler={handleCancelRecurrence}
                isLoading={isCancellingRecurrence}
              >
                <p>
                  This stops future auto-generated entries for this expense.
                  Past entries already created will not be affected.
                </p>
              </WarningDialog>
            </RoutedModal>
          </div>
        </>
      ) : (
        <secondaryComponents.FundTransfer
          closeWidget={() => setIsDisbursmentForm(false)}
          externalRequest={disbursementParameters}
        />
      )}
    </>
  );
};
export default ExpenseReview;
